import React from 'react';
import ReactDOM from 'react-dom';
import { Point, equal } from '../coreui';
import { localization } from '../../localization';
import { fromBack, getAreaTextTransforms, getIndexTransform, getLengthString, getLengthTextTransforms, indexOfMax, insertIntoPath, nonNull } from '../../react_utils';
import { FAButton, FixedPosDiv, ScaledNumberInput, Separator } from '../coreui/basic';
import { dataEqual, getDefaultName, getMousePoint, IMeasureToolProps, IMTInternalProps, ITool, MeasureToolUIBase } from './Base';
import { LineUIStyled } from './Line.styles';
import { RenderPathComplete, RenderText } from './SVGElements';
import ReactTooltip from 'react-tooltip';


export const lines = ['SingleLine', 'LineStrip', 'Polygon'] as const;
export type LineType = typeof lines[number];


const CreateLine = (type: LineType) => {
  interface ILineState {
    length: number;
    mouseOver: boolean;
    showContext?: {
      clientPos: Point;
      portal: number;
      joint?: number;
    };
    showTooltip?: Point;
    grabbing?: {
      portal: number;
      joint: number;
      move?: {
        lastPoint: Point;
        dist: number;
      }
    };
    renderText: {
      length: number;
      position: Point;
      angle: number;
    }[];
    area?: number;
    areaText?: {
      area: number;
      space: number;
      position: Point;
    }[];
    indexText?: {
      iAngle: { flipped: boolean; angle: number };
      iAnchor: Point;
    }
  };

  
  const interacting = (state: ILineState) => {
    return state.mouseOver || state.grabbing !== undefined || state.showContext !== undefined;
  };

  class MeasureToolLine extends React.Component<IMeasureToolProps & IMTInternalProps, ILineState> implements ITool {
    constructor(props: IMeasureToolProps & IMTInternalProps) {
      super(props);

      this.state = {
        length: 0,        // done in update state
        mouseOver: false,
        renderText: []
      };
    }

    private isHidden = () => (this.props.hidden & 1) === 1;
    private isCircHidden = () => (this.props.hidden & 2) === 2;
    private toggleHide = () => this.isHidden() ? (this.props.hidden & ~1) : (this.props.hidden | 1);
    private toggleCircHide = () => this.isCircHidden() ? (this.props.hidden & ~2) : (this.props.hidden | 2);

    private updateState = () => {
      const different = !equal(fromBack(this.props.data), fromBack(this.props.data, 1));
      const baseData = different ? this.props.data : this.props.data.slice(0, -1);
      const lengthUpdate = getLengthTextTransforms((type === 'Polygon' && baseData.length > 2) ? [...baseData, baseData[0]] : baseData);
      const areaUpdate = type === 'Polygon' ? getAreaTextTransforms(baseData) : undefined;
      const indexText = (this.props.onFinish === undefined && type !== 'Polygon') ? getIndexTransform(this.props.data) : undefined;

      this.setState({
        ...lengthUpdate,
        ...areaUpdate,
        indexText
      });
    };

    private insertJoint = (pIdx: number, clientPos: Point | MouseEvent) => {
      const portal = this.props.RenderPortals[pIdx].portal;
      if (portal && this.props.onUpdateData) {
        const data = type === 'Polygon' ? [...this.props.data, this.props.data[0]] : this.props.data;
        const newPoint = getMousePoint(clientPos, portal);
        const newPath = insertIntoPath(data, newPoint);
        if (this.state.grabbing)
          this.setState({ grabbing: { ...this.state.grabbing, joint: newPath.index } });
        else if (this.state.showContext)
          this.setState({ showContext: undefined });
        this.props.onUpdateData(newPath.path.slice(0, type === 'Polygon' ? -1 : undefined));
      }
    };

    private deleteJoint = (props: { joint: number, portal: number }) => {
      if (this.props.onUpdateData) {
        let data = [...this.props.data];
        data.splice(props.joint, 1);
        this.props.RenderPortals[props.portal].requestStyle!({});
        this.setState({ grabbing: undefined, mouseOver: false, showTooltip: undefined, showContext: undefined });
        if (data.length >= 2)
          this.props.onUpdateData(data);
        else
          this.props.onUpdateData(null);
      }
    };

    private mouseEnter = () => {
      if (!this.state.mouseOver) {
        this.setState({ mouseOver: true });
      }
    };

    private mouseLeave = () => {
      this.setState({ mouseOver: false });
    };

    private jointMouseDown = (e: React.MouseEvent<SVGElement, MouseEvent>, jointIndex: number, portal: number) => {
      if (e.button === 0 || e.button === 2) {
        this.props.RenderPortals[portal].requestStyle!({ cursor: 'grabbing' });
        var move = (type === 'SingleLine' && jointIndex === -1) || e.button === 2 ? {
          dist: 0, lastPoint: getMousePoint(e.nativeEvent, this.props.RenderPortals[portal].portal!)
        } : undefined;
        this.setState({ grabbing: { joint: jointIndex, portal, move }, showTooltip: move ? undefined : { x: e.clientX + 15, y: e.clientY + 15 } });
      }
    };

    private mouseUp = (e: MouseEvent) => {
      if ((e.button === 0 && this.state.grabbing) || (e.button === 2 && this.state.grabbing?.move)) {
        e.stopPropagation();
        const portal = this.state.grabbing.portal;
        this.props.RenderPortals[portal].requestStyle!({});
        if (this.state.grabbing.move && this.state.grabbing.move.dist < 10) {
          this.contextMenu({ e, portal, joint: this.state.grabbing.joint === -1 ? undefined : this.state.grabbing.joint });
        }
        this.setState({ grabbing: undefined, showTooltip: undefined });
      }
    };

    private mouseMove = (e: MouseEvent) => {
      if (this.props.onUpdateData && this.state.grabbing) {
        const pIndex  = this.state.grabbing.portal;
        const target = this.props.RenderPortals[pIndex].portal;
        if (target) {
          e.stopPropagation();
          const newPoint = getMousePoint(e, target);

          if (this.state.grabbing.move) {
            let move = { ...this.state.grabbing.move };
            const newPoint = getMousePoint(e, target);
            const dx = newPoint.x - move.lastPoint.x;
            const dy = newPoint.y - move.lastPoint.y;
            move.lastPoint = newPoint;
            move.dist += Math.sqrt(dx * dx + dy * dy);
            this.props.onUpdateData(this.props.data.map(({ x, y }) => ({ x: x + dx, y: y + dy })));
            this.setState({ grabbing: { ...this.state.grabbing, move } });
          } else {
            if (this.state.grabbing.joint === -1) {
              this.insertJoint(this.state.grabbing.portal, e);
            } else {
              let data = [...this.props.data];
              data[this.state.grabbing.joint] = newPoint;
              this.props.onUpdateData(data);
            }
            this.setState({ showTooltip: { x: e.clientX + 15, y: e.clientY + 15 } });
          }
        }
      }
    };

    private keyDown = (e: KeyboardEvent) => {
      if (e.key === 'Delete' && this.state.grabbing) {
        this.deleteJoint(this.state.grabbing);
      }
    };

    private contextMenu = (props?: { e: MouseEvent, portal: number, joint?: number }) => {
      if (props) {
        this.setState({
          showContext: { ...props, clientPos: { x: props.e.clientX, y: props.e.clientY } }
        });
      } else {
        this.setState({ showContext: undefined, mouseOver: false, grabbing: undefined });
      }
    };

    // return csv entries: => index; name; Unit; type; segment; Sx; Sy; Ex; Ey; Length; Area 
    public getCSVEntries = () => {
      const d = this.props.data;
      const ts = (val: number) => getLengthString(val, this.props.lengthScale);
      const name = this.props.name || `${localization[this.props.settings.lang].measurements.defaultName} ${this.props.index + 1}`;
      const toData = (p1: Point, p2: Point, length: number, segment: string, area?: number) => {
        const { x: sx, y: sy } = p1;
        const { x: ex, y: ey } = p2;
        return [
          (this.props.index + 1).toString(),
          name,
          this.props.unit,
          type,
          segment,
          ts(sx), ts(sy), ts(ex), ts(ey),
          ts(length),
          area === undefined ? '' : getLengthString(area, this.props.lengthScale ** 2)
        ];
      }
      const header = toData(d[0], type === 'Polygon' ? d[0] : fromBack(d), this.state.length, 'all', this.state.area);
      if (type === 'SingleLine') {
        return [header];
      }
      const segments = this.state.renderText.map(({ length }, i) => toData(d[i], d[(i + 1) % d.length], length, `${i + 1}`, this.state.area));
      return [header, ...segments];
    }

    componentDidMount = () => {
      this.updateState();
      document.addEventListener('mouseup', this.mouseUp);
      document.addEventListener('mousemove', this.mouseMove);
      document.addEventListener('keydown', this.keyDown);
      ReactTooltip.rebuild();
    };

    componentDidUpdate = (prevProps: IMeasureToolProps & IMTInternalProps, prevState: ILineState) => {
      if (!dataEqual(prevProps.data, this.props.data) || prevProps.lengthScale !== this.props.lengthScale) {
        const data = this.props.data;
        if (type === 'SingleLine' && this.props.onFinish && data.length > 2) {
          this.props.onFinish([data[0], data[data.length - 1]]);
        } else {
          this.updateState();
        }
      }
      if (this.props.onChangeInteracting) {
        const curInteracting = interacting(this.state);
        const prevInteracting = interacting(prevState);
        if (curInteracting !== prevInteracting)
          this.props.onChangeInteracting(curInteracting);
      }
      if (prevState.showContext !== this.state.showContext) {
        ReactTooltip.rebuild();
      }
    };

    componentWillUnmount = () => {
      document.removeEventListener('mouseup', this.mouseUp);
      document.removeEventListener('mousemove', this.mouseMove);
      document.removeEventListener('keydown', this.keyDown);
    };

    private renderBaseUI = (highlight: boolean) => {
      const loc = localization[this.props.settings.lang];
      const unitInput = <input value={this.props.unit}
        onChange={this.props.onUnitChanged ? e => this.props.onUnitChanged!(e.currentTarget.value) : undefined}
        disabled={this.props.onUnitChanged ? undefined : true} />
      
      const name = this.props.name || getDefaultName(this.props.index, this.props.settings.lang);
      const subUI = type === 'Polygon' ? (
        <LineUIStyled>
          <span>{loc.measurements.area}</span>
          <ScaledNumberInput value={this.state.area!} scale={this.props.lengthScale ** 2}
            onScaleChange={this.props.onLengthScaleChanged ? ns => this.props.onLengthScaleChanged!(Math.sqrt(ns)) : undefined} />
          <input value={`${this.props.unit}²`} disabled/>
          <FAButton icon={this.isHidden() ? 'eye-slash' : 'eye'} size={25}
            data-tip={loc.tooltips.toggleShowMeasurement( name ) }
            onClick={() => this.props.onHiddenChange(this.toggleHide())} />
          <span>{loc.measurements.circumference}</span>
          <ScaledNumberInput value={this.state.length} scale={this.props.lengthScale} onScaleChange={this.props.onLengthScaleChanged}/>
          { unitInput }
          <FAButton icon={this.isHidden() || this.isCircHidden() ? 'eye-slash' : 'eye'} size={25}
            data-tip={loc.tooltips.toggleShowMeasurement( `${name}: ${loc.measurements.circumference}` ) }
            onClick={() => this.props.onHiddenChange(this.toggleCircHide())} />
        </LineUIStyled>
      ) : (
          <LineUIStyled>
            <span>{loc.measurements.length}</span>
            <ScaledNumberInput value={this.state.length} scale={this.props.lengthScale} onScaleChange={this.props.onLengthScaleChanged}/>
            { unitInput }
            <FAButton icon={this.isHidden() ? 'eye-slash' : 'eye'}
              data-tip={loc.tooltips.toggleShowMeasurement( name ) }
              onClick={() => this.props.onHiddenChange(this.toggleHide())} />
          </LineUIStyled>
        );
      
      return (
        <MeasureToolUIBase {...this.props} grabbing={this.state.grabbing !== undefined} onMouseEnter={this.mouseEnter} onMouseLeave={this.mouseLeave} highlight={highlight}>
          {subUI}
        </MeasureToolUIBase>
      );
    }

    private renderContext = () => {
      const ctx = this.state.showContext;
      if (ctx) {
        const [msg, callback] = type === 'SingleLine' ? [null, null] : ctx.joint !== undefined ? [
          localization[this.props.settings.lang].deleteJointMessage,
          () => this.deleteJoint({ joint: ctx.joint!, portal: ctx.portal })
        ] : [
            localization[this.props.settings.lang].insertJointMessage,
            () => this.insertJoint(ctx.portal, ctx.clientPos)
        ];
        
        const header = (msg && callback) ? <>
          <button onClick={callback} style={{width: '100%'}}>{msg}</button>
          <Separator />
        </> : null;
        return (
          <FixedPosDiv pos={{ x: ctx.clientPos.x - 15, y: ctx.clientPos.y - 15 }} style={{
            width: 410, pointerEvents: 'all', backgroundColor: '#333333', borderRadius: 2, padding: 5
          }}
          onMouseMove={() => this.mouseEnter()}
          onMouseEnter={() => this.mouseEnter()}
          onMouseLeave={() => this.contextMenu()}
        >
          {header}
          {this.renderBaseUI(false)}
          </FixedPosDiv>
        );
      } else if (this.props.showTooltip || this.state.showTooltip) {
        const pos = this.props.showTooltip || this.state.showTooltip;
        const text = this.state.area ? `${getLengthString(this.state.area, this.props.lengthScale ** 2)} ${this.props.unit}²`
          : `${getLengthString(this.state.length, this.props.lengthScale)} ${this.props.unit}`
        return <FixedPosDiv pos={pos} style={{
          pointerEvents: 'none', fontSize: 25, whiteSpace: 'nowrap',
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
        }}>
          {text}
        </FixedPosDiv>
      }
      return null;
    };

    private renderSVGPath = (overlayScale: number, portalIndex: number, highlight: boolean) => {
      const allowPortalInteract = this.props.RenderPortals[portalIndex].requestStyle !== undefined && this.props.allowInteraction && !this.props.onFinish;
      return <g name={type}>
        <RenderPathComplete
          path={this.props.data}
          closed={type === 'Polygon'}
          allowPathGrab//={type !== 'SingleLine'}
          commonProps={{
            allowInteraction: allowPortalInteract,
            highlight,
            scale: overlayScale,
            grabbing: this.state.grabbing !== undefined
          }}
          pathCallbacks={ allowPortalInteract ? {
            onMouseEnter: this.mouseEnter,
            onMouseLeave: this.mouseLeave,
            onMouseMove: this.mouseEnter,
            onMouseDown: e => this.jointMouseDown(e, -1, portalIndex),
            //onContextMenu: e => this.contextMenu({ e: e.nativeEvent, portal: portalIndex })
          } : undefined}
          jointCallbacks={ allowPortalInteract ? {
            onMouseEnter: this.mouseEnter,
            onMouseLeave: this.mouseLeave,
            onMouseMove: this.mouseEnter,
            onMouseDown: (e, ji) => this.jointMouseDown(e, ji, portalIndex),
            //onContextMenu: (e, ji) => this.contextMenu({ e: e.nativeEvent, portal: portalIndex, joint: ji })
          } : undefined} />
      </g>
    };

    private renderSVGText = (overlayScale: number, hideText?: boolean) => {
      if (hideText)
        return null;
      
      const simplePost = ` ${this.props.unit}`;
      const textBase = this.isCircHidden() ? null : this.state.renderText.map(({ length, position, angle }, i, arr) => {
        const t = type === 'Polygon' ? 'joints' : arr.length === 1 ? 'caps' : (i === 0 || i === arr.length - 1) ? 'capjoint' : 'joints';
        return <RenderText key={i} type={t} pos={position} angle={angle} space={length} val={length * this.props.lengthScale} scale={overlayScale} postfix={simplePost} />
      });

      const areaText = this.state.areaText?.map(({ area, space, position }, i) => {
        const postfix = ` ${this.props.unit}²`;
        return <RenderText key={i} type='area' pos={position} angle={0} space={space} val={area * this.props.lengthScale ** 2} scale={overlayScale} postfix={postfix} />
      });

      let iText = this.state.indexText;
      if (this.state.areaText && this.state.areaText.length > 0 && this.props.onFinish === undefined) {
        const mIdx = indexOfMax(this.state.areaText.map(txt => txt.space));
        iText = {
          iAnchor: { x: this.state.areaText[mIdx].position.x, y: this.state.areaText[mIdx].position.y - 39 * overlayScale },
          iAngle: { angle: 0, flipped: false }
        };
      };

      const indexText = iText ? (
        <RenderText pos={iText.iAnchor} angle={iText.iAngle.angle} type={iText.iAngle.flipped ? 'index-flipped' : 'index'} space={0} val={this.props.index + 1} scale={overlayScale} />
      ) : null;

      let sumText : React.ReactElement | null = null;
      if (this.state.renderText.length > 1 && !this.isCircHidden()) {
        const mostSpaceIndex = indexOfMax(this.state.renderText.map(t => t.length));
        const item = this.state.renderText[mostSpaceIndex];
        const t = type === 'Polygon' ? 'joints' : (mostSpaceIndex === 0 || mostSpaceIndex === this.state.renderText.length - 1) ? 'capjoint' : 'joints';
        sumText = <RenderText below type={t} pos={item.position} angle={item.angle} space={item.length} val={this.state.length * this.props.lengthScale} scale={overlayScale} postfix={simplePost}/>
      }
        


      return <g name='text'>
        {indexText}
        {textBase}
        {sumText}
        {areaText}
      </g>;
    };

    public render() {
      const highlight = interacting(this.state);

      const BaseUI = this.renderBaseUI(highlight);

      const renderSVG = (overlayScale: number, portalIndex: number, hideText?: boolean) => {
        if (this.isHidden())
          return null;

        return <g name={this.props.name}>
          {this.renderSVGPath(overlayScale, portalIndex, highlight)}
          {this.renderSVGText(overlayScale, hideText)}
        </g>;
      };


      return (
        <>
          { this.props.UIPortals.filter(({ portal }) => nonNull(portal))
            .map(({ portal }, i) => ReactDOM.createPortal(BaseUI, portal!, i.toString()))}
          { this.props.RenderPortals.filter(({ portal }) => nonNull(portal))
            .map(({ portal, hideText, overlayScale }, i) => ReactDOM.createPortal(renderSVG(overlayScale, i, hideText), portal!, i.toString()))}
          { this.renderContext() }
        </>
      );
    }
  }

  return MeasureToolLine;
};
export const SingleLine = CreateLine('SingleLine');
export const LineStrip = CreateLine('LineStrip');
export const Polygon = CreateLine('Polygon');

const CreateIcon = (type: typeof SingleLine, data: Point[]) => {
  class Icon extends React.Component {
    private renderRef = React.createRef<SVGSVGElement>();
  
    componentDidMount = () => {
      this.forceUpdate();
    }
  
    public render() {
      return <>
        <svg ref={this.renderRef} width={50} height={50} />
        { React.createElement(type, {
          index: 0, unit: '', lengthScale: 1, settings: { lang: 'en', transitionDuration: 0.4 }, allowInteraction: false, name: 'Icon',
          data: data, UIPortals: [], hidden: 0, onHiddenChange: () => { },
          RenderPortals: [{ portal: this.renderRef.current, overlayScale: 0.75, hideText: true }]
        })}
      </>;
    }
  };
  return Icon;
};

export const SingleLineIcon = CreateIcon(SingleLine, [{ x: 11, y: 39 }, { x: 39, y: 11 }]);
export const LineStripIcon = CreateIcon(LineStrip, [{ x: 11, y: 39 }, { x: 39, y: 11 }, { x: 34, y: 34 }]);
export const PolygonIcon = CreateIcon(Polygon, [{ x: 11, y: 39 }, { x: 19, y: 19 }, { x: 39, y: 11 }, { x: 34, y: 34 }]);
